// Email template storage.
//
// Three surfaces read from here: the sysadmin Templates page (the global
// defaults in `emailTemplates/{id}`), the per-event Templates section
// (`events/{eventId}/emailTemplates/{id}`), and event creation, which prefills
// the per-event docs.
//
// The template *copy* is never bundled in this app — it lives in
// functions/lib/emailTemplates.js beside the code that sends it, and reaches
// the browser over the `getEmailTemplateDefaults` callable. That keeps exactly
// one copy of every body in the repo.

import { doc, getDocs, collection, serverTimestamp, setDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { auth, db, functions } from '../firebase'

export const TEMPLATE_LANGUAGES = ['es', 'en']
export const DEFAULT_TEMPLATE_LANGUAGE = 'es'

// One in-flight request per scope. Both the page and the event section ask for
// the defaults on mount, and the copy never changes between deploys.
const defaultsCache = new Map()

// The shape of one language's copy. A spec with `format: 'text'` (the reel
// share caption, which is not an email) holds `{text}`; every other template is
// an HTML email holding `{subject, html}`. Mirrors isEmailTemplate in
// functions/lib/emailTemplates.js.
const EMPTY_COPY = {
  html: { subject: '', html: '' },
  text: { text: '' },
}

/** 'text' for a plain-text template, else 'html'. */
export function templateFormat(spec) {
  return spec?.format === 'text' ? 'text' : 'html'
}

/** A blank copy in the shape `spec`'s format stores. */
export function emptyCopy(spec) {
  return { ...EMPTY_COPY[templateFormat(spec)] }
}

/**
 * Bundled defaults plus the template specs (scope, editableBy, placeholders,
 * format) and each template's sample values (`samples`).
 * Pass an eventId when the caller may be an event admin rather than a sysadmin;
 * they receive only the templates their role may edit.
 */
export function fetchTemplateDefaults(eventId) {
  const key = eventId || '*'
  if (!defaultsCache.has(key)) {
    const call = httpsCallable(functions, 'getEmailTemplateDefaults')
    defaultsCache.set(key, call(eventId ? { eventId } : {})
      .then((res) => res.data)
      .catch((err) => {
        // Don't cache a failure: a transient error would otherwise stick for
        // the lifetime of the tab.
        defaultsCache.delete(key)
        throw err
      }))
  }
  return defaultsCache.get(key)
}

/** Every global default that has been overridden, as `{templateId: content}`. */
export async function fetchGlobalTemplates() {
  const snap = await getDocs(collection(db, 'emailTemplates'))
  const out = {}
  snap.forEach((d) => { out[d.id] = d.data().content || null })
  return out
}

export async function saveGlobalTemplate(templateId, content) {
  await setDoc(
    doc(db, 'emailTemplates', templateId),
    { content, updatedAt: serverTimestamp() },
    { merge: true }
  )
}

/** A single event's overrides, as `{templateId: content}`. */
export async function fetchEventTemplates(eventId) {
  const snap = await getDocs(collection(db, 'events', eventId, 'emailTemplates'))
  const out = {}
  snap.forEach((d) => { out[d.id] = d.data().content || null })
  return out
}

export async function saveEventTemplate(eventId, templateId, content) {
  await setDoc(
    doc(db, 'events', eventId, 'emailTemplates', templateId),
    { content, updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.uid || null },
    { merge: true }
  )
}

/**
 * Seed a new event's templates from the current defaults, and record which
 * language its mail goes out in.
 *
 * Prefilling is a convenience, not a correctness requirement: the sender falls
 * back event doc → global doc → bundled copy, so an event with no documents
 * here still sends. It exists so the event's editor opens on real copy, and so
 * a later change to a default does not silently reword an existing event's mail.
 */
export async function setEventEmailLanguage(eventId, lang) {
  const language = TEMPLATE_LANGUAGES.includes(lang) ? lang : DEFAULT_TEMPLATE_LANGUAGE
  await setDoc(doc(db, 'events', eventId), { emailLanguage: language }, { merge: true })
  return language
}

// Server copy: prefillEventTemplates in admin/functions/lib/events.js, for
// weddings created by the createWeddingEvent callable. Keep them in step.
export async function prefillEventTemplates(eventId, lang) {
  await setEventEmailLanguage(eventId, lang)

  const { templates, specs } = await fetchTemplateDefaults()
  const globals = await fetchGlobalTemplates()

  for (const [templateId, spec] of Object.entries(specs)) {
    if (spec.scope !== 'event') continue
    const content = globals[templateId] || templates[templateId]
    if (!content) continue
    await setDoc(
      doc(db, 'events', eventId, 'emailTemplates', templateId),
      { content, updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.uid || null }
    )
  }
}

/**
 * The copy an editor should open on: the stored override, else the global
 * default, else the bundled default. Mirrors the sender's fallback chain so
 * what an admin sees is what would go out. `spec` picks the shape: `{text}`
 * for a plain-text template, `{subject, html}` otherwise.
 */
export function resolveContent({ stored, globalContent, bundled, spec }) {
  const sources = [stored, globalContent, bundled]
  const fields = Object.keys(emptyCopy(spec))
  const out = {}
  for (const code of TEMPLATE_LANGUAGES) {
    const copy = sources.find((s) => fields.some((f) => s?.[code]?.[f]))
    out[code] = { ...emptyCopy(spec), ...(copy?.[code] || {}) }
  }
  return out
}

/**
 * Send one template to an address, rendered against sample values. Carries the
 * editor's current draft rather than what is stored, so a change can be seen
 * before it is saved. Testing only — the subject arrives with a [TEST] prefix.
 */
export async function sendTestEmail({ templateId, lang, subject, html, to, eventId }) {
  const call = httpsCallable(functions, 'sendTestEmail')
  const res = await call({ templateId, lang, subject, html, to, eventId })
  return res.data
}

const PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/g

/**
 * Template HTML with each placeholder swapped for a highlighted token, for the
 * preview pane. Never used to send: the real substitution and escaping happen
 * in functions/lib/emailTemplates.js.
 */
export function previewHtml(html) {
  return String(html || '').replace(PLACEHOLDER, (_, name) =>
    `<span style="background:#fde68a;color:#111;border-radius:3px;padding:0 3px;">${name}</span>`
  )
}

export function previewSubject(subject) {
  return String(subject || '').replace(PLACEHOLDER, (_, name) => `[${name}]`)
}

/**
 * A plain-text template as it would read, placeholders filled from the sample
 * values the defaults callable hands back. One with no sample shows as
 * `[name]`, like the subject preview.
 */
export function previewText(text, samples) {
  return String(text || '').replace(PLACEHOLDER, (_, name) => {
    const value = samples?.[name]
    return value === undefined || value === null ? `[${name}]` : String(value)
  })
}

/**
 * Placeholders used by a copy (subject/html, or text) that the sender does not
 * fill. A typo like {{eventname}} would otherwise ship verbatim to a recipient.
 */
export function unknownPlaceholders(copy, allowed) {
  const known = new Set(allowed || [])
  const found = new Set()
  const all = `${copy?.subject || ''}${copy?.html || ''}${copy?.text || ''}`
  for (const [, name] of all.matchAll(PLACEHOLDER)) {
    if (!known.has(name)) found.add(name)
  }
  return [...found]
}
